@file:OptIn(ExperimentalSerializationApi::class, ExperimentalSerializationApi::class)
@file:Suppress("unused")

package tk.mallumo.common.msg

import kotlinx.datetime.*
import kotlinx.serialization.*
import kotlinx.serialization.protobuf.*
import org.ssoft.common.tools.*
import org.ssoft.server.common.msg.PROTO_INSTANCE_MSG

data class MessageDecodeResult(
    val msg: Message,
    val data: ByteArray?
) {
    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other == null || this::class != other::class) return false

        other as MessageDecodeResult

        if (msg != other.msg) return false
        if (!data.contentEquals(other.data)) return false

        return true
    }

    override fun hashCode(): Int {
        var result = msg.hashCode()
        result = 31 * result + (data?.contentHashCode() ?: 0)
        return result
    }
}

/**
 * package must stay as it is because of polymorph serializer
 */
@Serializable
sealed interface Message {
    val id: Long
    val type: Int
    var source: String
    var target: String
    var statusInt: Int
    var statusStr: String

    fun swapTargetToSource() {
        val src = source
        source = target
        target = src
    }

    fun setupStatus(code: Int, msg: String = "") {
        statusInt = code
        statusStr = msg
    }

    companion object {

        const val TYPE_DATA_SEND = 0
        const val TYPE_DATA_RECEIVE = 1
        const val TYPE_SCREENSHOT = 2
        const val TYPE_CMD = 3
        const val TYPE_REPORT_SYSTEM = 4
        const val TYPE_REPORT_PLAYLIST = 5
        const val TYPE_REPORT_CONNECTION = 6
        const val TYPE_STATUS = 7
        const val TYPE_TASK = 8
        const val TYPE_DISPLAYSHOT = 9
        const val TYPE_REPORT_APP = 10
        const val TYPE_REPORT_GAME = 11
        const val TYPE_GAME_ACTION = 12

        const val TARGET_REPORTS = "REPORTS"
        const val TARGET_GAMES = "GAMES"

        const val STATUS_OK = 0
        const val STATUS_NOT_CONNECTED = -1
        const val STATUS_INVALID_INPUT = -2
        const val STATUS_FAIL = -3

        fun decode(data: ByteArray): MessageDecodeResult {
//            val type = data.copyOfRange(0, 4).toInt()
            val protoSize = data.copyOfRange(4, 8).toInt()
            val protoBytes = data.copyOfRange(8, protoSize + 8)

            val extras = data.takeIf { it.size > protoSize + 8 }?.let {
                data.copyOfRange(protoSize + 8, data.size)
            }
            return MessageDecodeResult(
                msg = PROTO_INSTANCE_MSG.decodeFromByteArray<Message>(protoBytes),
                data = extras
            )
        }

        fun encode(msg: Message, extras: ByteArray? = null): ByteArray {
            val protoBytes = PROTO_INSTANCE_MSG.encodeToByteArray(msg)
            val output = ByteArray(8 + protoBytes.size + (extras?.size ?: 0))

            msg.type.toByteArray().copyInto(output)
            protoBytes.size.toByteArray().copyInto(output, 4)
            protoBytes.copyInto(output, 8)
            extras?.copyInto(output, 8 + protoBytes.size)
            return output
        }
    }

    /**
     * status of msg send
     */
    @Serializable
    data class Status(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_STATUS,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String
    ) : Message {
        companion object {

            fun createNotConnected(msg: Message, extras: String? = null): ByteArray {
                return create(STATUS_NOT_CONNECTED, extras ?: "Device not connected", msg)
            }

            fun createOk(msg: Message, extras: String? = null): ByteArray {
                return create(STATUS_OK, extras ?: "Send OK", msg)
            }

            fun createInvalidInput(msg: Message, extras: String? = null): ByteArray {
                return create(STATUS_INVALID_INPUT, extras, msg)
            }

            private fun create(state: Int, extras: String?, msg: Message): ByteArray {
                return encode(
                    Status(
                        id = msg.id,
                        type = TYPE_STATUS,
                        source = msg.source,
                        target = msg.target,
                        statusInt = state,
                        statusStr = extras ?: ""
                    ), null
                )
            }
        }
    }

    /**
     * Send data from server to device
     */
    @Serializable
    data class DataSend(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_DATA_SEND,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        val path: String
    ) : Message

    /**
     * Receive data from device to server
     */
    @Serializable
    data class DataReceive(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_DATA_RECEIVE,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        val path: String
    ) : Message


    /**
     * capture screenshot
     */
    @Serializable
    data class Screenshot(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_SCREENSHOT,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
    ) : Message

    /**
     * capture screenshot
     */
    @Serializable
    data class GameAction(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_GAME_ACTION,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String = TARGET_GAMES,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        var id_game: Long,
        @ProtoNumber(8)
        var type_game: Int,
        @ProtoNumber(9)
        var session: String,
        @ProtoNumber(10)
        var token: Long,
    ) : Message

    /**
     * capture screenshot
     */
    @Serializable
    data class Displayshot(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_DISPLAYSHOT,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
    ) : Message

    /**
     * execute command
     */
    @Serializable
    data class Cmd(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_CMD,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        val command: String,
        @ProtoNumber(8)
        val timeoutMs: Long = 5000,
    ) : Message


    @Serializable
    data class ReportSystem(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_REPORT_SYSTEM,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String = TARGET_REPORTS,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        val id_device: Long,
        @ProtoNumber(8)
        val dt: LocalDateTime,
    ) : Message

    @Serializable
    data class ReportApp(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_REPORT_APP,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String = TARGET_REPORTS,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        val id_device: Long,
        @ProtoNumber(8)
        val dt: LocalDateTime,
        @ProtoNumber(9)
        val id_app: Long,
        @ProtoNumber(10)
        val type_app: Int,
        @ProtoNumber(11)
        val revision: Int,
    ) : Message

    @Serializable
    data class ReportConnection(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_REPORT_CONNECTION,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String = TARGET_REPORTS,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        var connected: Boolean,
        @ProtoNumber(8)
        val dt: LocalDateTime,
    ) : Message

    @Serializable
    data class ReportPlaylist(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_REPORT_PLAYLIST,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String = TARGET_REPORTS,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        val id_device: Long,
        @ProtoNumber(8)
        val dt: LocalDateTime,
    ) : Message

    @Serializable
    data class ReportGame(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_REPORT_GAME,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String = TARGET_REPORTS,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        val id_device: Long,
        @ProtoNumber(8)
        val id_game: Long,
        @ProtoNumber(9)
        val type_game: Int,
        @ProtoNumber(10)
        val dt: LocalDateTime,
    ) : Message

    @Serializable
    data class Task(
        @ProtoNumber(1)
        override val id: Long,
        @ProtoNumber(2)
        override val type: Int = TYPE_TASK,
        @ProtoNumber(3)
        override var source: String,
        @ProtoNumber(4)
        override var target: String,
        @ProtoNumber(5)
        override var statusInt: Int,
        @ProtoNumber(6)
        override var statusStr: String,
        @ProtoNumber(7)
        val key: String
    ) : Message {
        enum class Keys {
            UNKNOWN,

            //            RELOAD_RESOURCES,
//            RELOAD_CACHE,
            RESTART_APP,
            UPDATE,
            REBOOT_DEVICE,
            REGISTRATION,
            DELETE,
            APPS,
            PLAYLIST,
            CONFIGURATION,
            REMOTE_TOUCHSCREEN,
            RSS;

            companion object {
                operator fun get(key: String?) = kotlin
                    .runCatching { valueOf(key!!) }
                    .getOrNull()
                    ?: UNKNOWN
            }
        }
    }

    @Serializable
    data class TouchscreenEvent(
        val mode: Int,
        val amount: Int,
        val x: Int,
        val y: Int
    ) {
        companion object {
            val MODE_CLICK = 4
            val MODE_SCROLL = 2
        }
    }
}
